import React, { useState, useEffect } from "react";
import {
  X,
  Banknote,
  Calendar as CalendarIcon,
  AlertCircle,
  Clock,
  Sparkles,
  CheckCircle2,
} from "lucide-react";
import { loansService, loanTypesService } from "../services/api";
import {
  toPersianNumbers,
  toEnglishNumbers,
  formatCurrency,
} from "../utils/numbers";
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { Modal } from "../components/Modal";
import { SearchableSelect } from "../components/SearchableSelect";

interface Loan {
  id: number;
  user_id: number;
  amount: string | number;
  requested_date: string;
  description: string;
  collateral?: string;
  status:
    | "pending"
    | "waiting_user_approval"
    | "user_approved"
    | "approved_registration"
    | "fee_paid"
    | "paid"
    | "rejected";
  user_role?: string;
  first_name: string;
  last_name: string;
  national_code: string;
  mobile: string;
  loan_type_id?: number;
  loan_type_name?: string;
  percentage?: number;
  installments_count?: number;
  requested_percentage?: number;
  requested_installments_count?: number;
  registration_fee_percentage?: number;
  admin_notes?: string;
  user_notes?: string;
  negotiation_history?: string;
}

export default function LoanReservations() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user?.role === "admin";

  const [loans, setLoans] = useState<Loan[]>([]);
  const [loading, setLoading] = useState(true);
  const [loanTypes, setLoanTypes] = useState<any[]>([]);

  // Filter Tab
  const [activeTab, setActiveTab] = useState<
    "all" | "pending" | "waiting_user_approval" | "user_approved"
  >("all");

  // Manager Proposal Modal State
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [approveData, setApproveData] = useState({
    loan_type_id: "",
    percentage: "",
    installments_count: "",
    registration_fee_percentage: "",
    admin_notes: "",
  });

  // Final Approval Modal State
  const [selectedLoanForFinal, setSelectedLoanForFinal] = useState<Loan | null>(
    null,
  );
  const [isFinalModalOpen, setIsFinalModalOpen] = useState(false);
  const [finalSubmitting, setFinalSubmitting] = useState(false);

  // General Notification Modal
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "warning" | "info";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
  });

  useEffect(() => {
    fetchLoans();
    fetchLoanTypes();
  }, []);

  const fetchLoans = async () => {
    setLoading(true);
    try {
      const response = await loansService.getLoans();
      let loansArray = Array.isArray(response) ? response : response.data || [];
      // Keep only initial registration stages
      loansArray = loansArray.filter((loan: Loan) =>
        ["pending", "waiting_user_approval", "user_approved"].includes(
          loan.status,
        ),
      );

      // Sort by requested date (oldest first)
      loansArray = loansArray.sort((a: Loan, b: Loan) => {
        const parseParts = (d?: string) => {
          if (!d) return null;
          const parts = d.split(/[\/\-]/).map((p) => parseInt(p, 10));
          if (parts.length === 3 && parts.every((n) => !isNaN(n))) {
            return parts[0] * 10000 + parts[1] * 100 + parts[2];
          }
          return null;
        };
        const numA = parseParts(a.requested_date);
        const numB = parseParts(b.requested_date);
        if (numA !== null && numB !== null) return numA - numB;
        const timeA = new Date(a.requested_date).getTime();
        const timeB = new Date(b.requested_date).getTime();
        if (!isNaN(timeA) && !isNaN(timeB)) return timeA - timeB;
        return String(a.requested_date || "").localeCompare(
          String(b.requested_date || ""),
        );
      });

      setLoans(loansArray);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching loans:", error);
      setLoans([]);
      setLoading(false);
    }
  };

  const fetchLoanTypes = async () => {
    try {
      const response = await loanTypesService.getLoanTypes();
      setLoanTypes(response.loanTypes || []);
    } catch (error) {
      console.error("Error fetching loan types:", error);
    }
  };

  // Open Proposal Modal (Admin setting or modifying terms to send to user)
  const handleOpenProposalModal = (loan: Loan) => {
    setSelectedLoan(loan);

    let calcFee = "1.5";
    try {
      const today = new DateObject({ calendar: persian, locale: persian_fa });
      const currentMonthStr = toEnglishNumbers(today.format("YYYY/MM"));
      const isCurrentMonth = loan.requested_date.startsWith(currentMonthStr);
      if (!isCurrentMonth) {
        if (loan.user_role === "investor" || loan.user_role === "admin") {
          calcFee = "0.5";
        } else {
          calcFee = "1";
        }
      }
    } catch (e) {
      console.error(e);
    }

    const initialPerc =
      loan.percentage != null
        ? String(loan.percentage)
        : loan.requested_percentage != null
          ? String(loan.requested_percentage)
          : "";
    const initialInst =
      loan.installments_count != null
        ? String(loan.installments_count)
        : loan.requested_installments_count != null
          ? String(loan.requested_installments_count)
          : "12";

    setApproveData({
      loan_type_id: loan.loan_type_id ? String(loan.loan_type_id) : "",
      percentage: initialPerc,
      installments_count: initialInst,
      registration_fee_percentage:
        loan.registration_fee_percentage != null
          ? String(loan.registration_fee_percentage)
          : calcFee,
      admin_notes: loan.admin_notes || "",
    });

    setIsApproveModalOpen(true);
  };

  // Submit Proposal to User
  const handleSendProposal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLoan) return;

    if (
      !approveData.loan_type_id ||
      !approveData.percentage ||
      !approveData.installments_count
    ) {
      setModalState({
        isOpen: true,
        title: "خطا",
        message: "تمامی فیلدها الزامی است.",
        type: "error",
      });
      return;
    }

    const percentage = Number(toEnglishNumbers(approveData.percentage));
    const installments_count = parseInt(
      toEnglishNumbers(approveData.installments_count),
      10,
    );

    if (percentage > 15 || percentage < 0) {
      setModalState({
        isOpen: true,
        title: "خطا",
        message: "درصد کارمزد باید بین ۰ تا ۱۵ درصد باشد.",
        type: "error",
      });
      return;
    }

    if (installments_count < 1 || installments_count > 36) {
      setModalState({
        isOpen: true,
        title: "خطا",
        message: "تعداد اقساط باید بین ۱ تا ۳۶ ماه باشد.",
        type: "error",
      });
      return;
    }

    try {
      await loansService.updateLoanStatus(
        selectedLoan.id,
        "waiting_user_approval",
        {
          loan_type_id: approveData.loan_type_id,
          percentage,
          installments_count,
          registration_fee_percentage: Number(
            toEnglishNumbers(approveData.registration_fee_percentage),
          ),
          admin_notes: approveData.admin_notes,
        },
      );
      fetchLoans();
      setIsApproveModalOpen(false);
      setModalState({
        isOpen: true,
        title: "موفق",
        message:
          "شرایط پیشنهادی برای متقاضی ارسال شد و در انتظار تایید یا نظر کاربر قرار گرفت.",
        type: "success",
      });
    } catch (error: any) {
      setModalState({
        isOpen: true,
        title: "خطا",
        message:
          error.response?.data?.message || "خطا در ارسال شرایط به متقاضی",
        type: "error",
      });
    }
  };

  // Open Final Approval Modal (when user has approved terms)
  const handleOpenFinalModal = (loan: Loan) => {
    setSelectedLoanForFinal(loan);
    setIsFinalModalOpen(true);
  };

  // Submit Final Approval by Admin
  const handleConfirmFinalApproval = async () => {
    if (!selectedLoanForFinal) return;
    setFinalSubmitting(true);
    try {
      await loansService.updateLoanStatus(
        selectedLoanForFinal.id,
        "approved_registration",
        {
          loan_type_id: selectedLoanForFinal.loan_type_id,
          percentage: selectedLoanForFinal.percentage,
          installments_count: selectedLoanForFinal.installments_count,
          registration_fee_percentage:
            selectedLoanForFinal.registration_fee_percentage,
        },
      );
      fetchLoans();
      setIsFinalModalOpen(false);
      setModalState({
        isOpen: true,
        title: "موفق",
        message:
          "ثبت نام اولیه وام تایید نهایی شد و به بخش پرداخت کارمزد منتقل گردید.",
        type: "success",
      });
    } catch (error: any) {
      setModalState({
        isOpen: true,
        title: "خطا",
        message: error.response?.data?.message || "خطا در تایید نهایی وام",
        type: "error",
      });
    } finally {
      setFinalSubmitting(false);
    }
  };

  const handleReject = async (id: number) => {
    if (!window.confirm("آیا از رد این درخواست اطمینان دارید؟")) return;

    try {
      await loansService.updateLoanStatus(id, "rejected");
      fetchLoans();
      setModalState({
        isOpen: true,
        title: "موفق",
        message: "درخواست با موفقیت رد شد.",
        type: "success",
      });
    } catch (error: any) {
      setModalState({
        isOpen: true,
        title: "خطا",
        message: error.response?.data?.message || "خطا در رد درخواست",
        type: "error",
      });
    }
  };

  // Filtered Loans based on Active Tab
  const filteredLoans = loans.filter((loan) => {
    if (activeTab === "all") return true;
    return loan.status === activeTab;
  });

  const pendingCount = loans.filter((l) => l.status === "pending").length;
  const waitingUserCount = loans.filter(
    (l) => l.status === "waiting_user_approval",
  ).length;
  const userApprovedCount = loans.filter(
    (l) => l.status === "user_approved",
  ).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            ثبت اولیه و مذاکره وام‌ها
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            بررسی، پیشنهاد شرایط کارمزد و اقساط، و تایید نهایی درخواست‌های وام
            کاربران
          </p>
        </div>
        {!isAdmin && (
          <span className="px-3.5 py-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 rounded-xl text-xs font-semibold w-fit">
            حالت فقط مشاهده (سرمایه‌گذار)
          </span>
        )}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 p-1.5 bg-slate-100 dark:bg-slate-800/80 rounded-2xl border border-slate-200 dark:border-slate-700/80 w-fit">
        <button
          onClick={() => setActiveTab("all")}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
            activeTab === "all"
              ? "bg-white dark:bg-slate-700 text-persian-600 dark:text-persian-300 shadow-sm"
              : "text-slate-600 dark:text-slate-400 hover:text-slate-900"
          }`}
        >
          <span>همه</span>
          <span className="px-1.5 py-0.5 rounded-full text-[11px] bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200">
            {toPersianNumbers(loans.length)}
          </span>
        </button>

        <button
          onClick={() => setActiveTab("pending")}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
            activeTab === "pending"
              ? "bg-white dark:bg-slate-700 text-persian-600 dark:text-persian-300 shadow-sm"
              : "text-slate-600 dark:text-slate-400 hover:text-slate-900"
          }`}
        >
          <span>در انتظار بررسی/پیشنهاد مدیر</span>
          {pendingCount > 0 && (
            <span className="px-1.5 py-0.5 rounded-full text-[11px] bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 font-bold">
              {toPersianNumbers(pendingCount)}
            </span>
          )}
        </button>

        <button
          onClick={() => setActiveTab("waiting_user_approval")}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
            activeTab === "waiting_user_approval"
              ? "bg-white dark:bg-slate-700 text-purple-600 dark:text-purple-300 shadow-sm"
              : "text-slate-600 dark:text-slate-400 hover:text-slate-900"
          }`}
        >
          <span>منتظر تایید متقاضی</span>
          {waitingUserCount > 0 && (
            <span className="px-1.5 py-0.5 rounded-full text-[11px] bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 font-bold">
              {toPersianNumbers(waitingUserCount)}
            </span>
          )}
        </button>

        <button
          onClick={() => setActiveTab("user_approved")}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
            activeTab === "user_approved"
              ? "bg-white dark:bg-slate-700 text-emerald-600 dark:text-emerald-300 shadow-sm"
              : "text-slate-600 dark:text-slate-400 hover:text-slate-900"
          }`}
        >
          <span className="flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            <span>تایید شده توسط متقاضی (آماده تایید نهایی)</span>
          </span>
          {userApprovedCount > 0 && (
            <span className="px-1.5 py-0.5 rounded-full text-[11px] bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-300 font-bold">
              {toPersianNumbers(userApprovedCount)}
            </span>
          )}
        </button>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-right border-collapse whitespace-nowrap">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                <th className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  درخواست‌دهنده
                </th>
                <th className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  کد ملی / موبایل
                </th>
                <th className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  مبلغ وام
                </th>
                <th className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  شرایط درخواستی / فعلی
                </th>
                <th className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  تاریخ درخواستی
                </th>
                <th className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  وثیقه
                </th>
                <th className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  وضعیت مذاکره
                </th>
                <th className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  عملیات
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {loading ? (
                <tr>
                  <td
                    colSpan={8}
                    className="p-8 text-center text-slate-500 dark:text-slate-400"
                  >
                    <div className="flex flex-col items-center justify-center space-y-4">
                      <div className="w-8 h-8 border-4 border-persian-600 border-t-transparent rounded-full animate-spin"></div>
                      <p>در حال دریافت اطلاعات...</p>
                    </div>
                  </td>
                </tr>
              ) : filteredLoans.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="p-8 text-center text-slate-500 dark:text-slate-400"
                  >
                    <div className="flex flex-col items-center justify-center gap-2 text-slate-400">
                      <AlertCircle className="w-8 h-8" />
                      <p>درخواستی در این بخش وجود ندارد.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredLoans.map((loan) => (
                  <tr
                    key={loan.id}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    <td className="p-4 font-medium text-slate-900 dark:text-white">
                      <div>
                        {loan.first_name} {loan.last_name}
                      </div>
                      {loan.user_role && loan.user_role !== "user" && (
                        <span className="text-[10px] text-persian-600 font-normal">
                          نقش: {loan.user_role}
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-slate-600 dark:text-slate-300">
                      <div className="text-xs">
                        {toPersianNumbers(loan.national_code)}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        {toPersianNumbers(loan.mobile)}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300 font-medium">
                        <Banknote className="w-4 h-4 text-slate-400" />
                        {formatCurrency(loan.amount)}
                      </div>
                    </td>
                    <td className="p-4">
                      {loan.status === "user_approved" ? (
                        <div className="flex flex-col gap-0.5 text-xs">
                          <span className="font-bold text-emerald-700 dark:text-emerald-400">
                            تایید متقاضی:{" "}
                            {toPersianNumbers(loan.percentage ?? 0)}٪ -{" "}
                            {toPersianNumbers(loan.installments_count ?? 0)} ماه
                          </span>
                          {loan.user_notes && (
                            <span className="text-[10px] text-slate-500 dark:text-slate-400 line-clamp-1">
                              پیام کاربر: {loan.user_notes}
                            </span>
                          )}
                        </div>
                      ) : loan.status === "waiting_user_approval" ? (
                        <div className="flex flex-col gap-0.5 text-xs">
                          <span className="font-bold text-purple-700 dark:text-purple-300">
                            پیشنهاد مدیر:{" "}
                            {toPersianNumbers(loan.percentage ?? 0)}٪ -{" "}
                            {toPersianNumbers(loan.installments_count ?? 0)} ماه
                          </span>
                          {loan.admin_notes && (
                            <span className="text-[10px] text-slate-500 dark:text-slate-400 line-clamp-1">
                              توضیح: {loan.admin_notes}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col gap-0.5 text-xs">
                          <span className="font-semibold text-amber-700 dark:text-amber-400">
                            درخواست کاربر:{" "}
                            {toPersianNumbers(
                              loan.requested_percentage ?? loan.percentage ?? 0,
                            )}
                            ٪ -{" "}
                            {toPersianNumbers(
                              loan.requested_installments_count ??
                                loan.installments_count ??
                                0,
                            )}{" "}
                            ماه
                          </span>
                          {loan.user_notes && (
                            <span className="text-[10px] text-blue-600 dark:text-blue-400 line-clamp-1 font-medium">
                              پیشنهاد متقابل: {loan.user_notes}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300 text-xs">
                        <CalendarIcon className="w-4 h-4 text-slate-400" />
                        {toPersianNumbers(loan.requested_date)}
                      </div>
                    </td>
                    <td className="p-4 text-slate-700 dark:text-slate-300 text-xs">
                      {loan.collateral === "gold"
                        ? "طلا"
                        : loan.collateral === "simcard"
                          ? "سیم کارت"
                          : loan.collateral === "dollar"
                            ? "دلار"
                            : loan.collateral === "document"
                              ? "سند"
                              : loan.collateral === "credit"
                                ? "اعتبار صندوق"
                                : "-"}
                    </td>
                    <td className="p-4">
                      {loan.status === "user_approved" ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          تایید شده توسط متقاضی
                        </span>
                      ) : loan.status === "waiting_user_approval" ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
                          <Clock className="w-3.5 h-3.5" />
                          منتظر تایید متقاضی
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                          <Sparkles className="w-3.5 h-3.5" />
                          {loan.user_notes
                            ? "پیشنهاد جدید کاربر"
                            : "در انتظار بررسی مدیر"}
                        </span>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-start gap-2">
                        {!isAdmin ? (
                          <span className="text-xs text-slate-400 font-medium px-2 py-1">
                            فقط مشاهده
                          </span>
                        ) : loan.status === "user_approved" ? (
                          <>
                            <button
                              onClick={() => handleOpenFinalModal(loan)}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm shadow-emerald-900/20 cursor-pointer"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                              <span>تایید نهایی ثبت نام</span>
                            </button>
                            <button
                              onClick={() => handleOpenProposalModal(loan)}
                              className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300 rounded-lg text-xs transition-colors"
                              title="تغییر شرایط"
                            >
                              ویرایش
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleOpenProposalModal(loan)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-persian-50 text-persian-700 dark:bg-persian-900/30 dark:text-persian-300 hover:bg-persian-100 rounded-lg text-xs font-bold transition-colors cursor-pointer border border-persian-200 dark:border-persian-800/50"
                          >
                            <Sparkles className="w-3.5 h-3.5" />
                            <span>
                              {loan.status === "waiting_user_approval"
                                ? "ویرایش شرایط ارسالی"
                                : "بررسی و پیشنهاد شرایط"}
                            </span>
                          </button>
                        )}

                        {isAdmin && (
                          <button
                            onClick={() => handleReject(loan.id)}
                            className="flex items-center gap-1 px-2.5 py-1.5 bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400 hover:bg-red-100 rounded-lg text-xs font-medium transition-colors cursor-pointer"
                            title="رد درخواست"
                          >
                            <X className="w-3.5 h-3.5" />
                            <span>رد</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Manager Proposal Modal */}
      {isApproveModalOpen && selectedLoan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-lg my-8 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="p-5 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-persian-500/10 via-purple-500/10 to-transparent flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-persian-600 text-white flex items-center justify-center shadow">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                    تعیین شرایط وام (پیشنهاد مدیر به متقاضی)
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    متقاضی: {selectedLoan.first_name} {selectedLoan.last_name}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsApproveModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg"
              >
                ✕
              </button>
            </div>

            <div className="p-5 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700 space-y-3">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="text-slate-500 dark:text-slate-400 block mb-0.5">
                    مبلغ وام
                  </span>
                  <span className="font-bold text-persian-600 dark:text-persian-400 text-sm">
                    {formatCurrency(selectedLoan.amount)}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 dark:text-slate-400 block mb-0.5">
                    تاریخ درخواستی
                  </span>
                  <span className="font-medium text-slate-900 dark:text-white">
                    {toPersianNumbers(selectedLoan.requested_date)}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 dark:text-slate-400 block mb-0.5">
                    کد ملی
                  </span>
                  <span className="font-medium text-slate-900 dark:text-white">
                    {toPersianNumbers(selectedLoan.national_code)}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 dark:text-slate-400 block mb-0.5">
                    شماره موبایل
                  </span>
                  <span className="font-medium text-slate-900 dark:text-white">
                    {toPersianNumbers(selectedLoan.mobile)}
                  </span>
                </div>
              </div>

              {/* User Requested Terms Display */}
              <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 rounded-xl space-y-1.5 text-xs">
                <div className="flex items-center justify-between font-bold text-amber-900 dark:text-amber-300">
                  <div className="flex items-center gap-1.5">
                    <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                    <span>آخرین درخواست کاربر:</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>
                      کارمزد:{" "}
                      {toPersianNumbers(
                        selectedLoan.requested_percentage ??
                          selectedLoan.percentage ??
                          0,
                      )}
                      ٪
                    </span>
                    <span>•</span>
                    <span>
                      اقساط:{" "}
                      {toPersianNumbers(
                        selectedLoan.requested_installments_count ??
                          selectedLoan.installments_count ??
                          0,
                      )}{" "}
                      ماه
                    </span>
                  </div>
                </div>
                {selectedLoan.user_notes && (
                  <p className="text-[11px] text-amber-800 dark:text-amber-300 pt-1 border-t border-amber-200/60 dark:border-amber-800/40">
                    <strong>پیام کاربر:</strong> {selectedLoan.user_notes}
                  </p>
                )}
              </div>

              {/* History if available */}
              {selectedLoan.negotiation_history &&
                (() => {
                  try {
                    const history = JSON.parse(
                      selectedLoan.negotiation_history,
                    );
                    if (Array.isArray(history) && history.length > 1) {
                      return (
                        <div className="text-[11px] space-y-1 max-h-24 overflow-y-auto pr-1">
                          <span className="font-semibold text-slate-500 block">
                            سابقه پیشنهادات:
                          </span>
                          {history.map((h: any, i: number) => (
                            <div
                              key={i}
                              className="text-slate-600 dark:text-slate-400 flex justify-between"
                            >
                              <span>
                                {h.role === "admin" ? "مدیر:" : "کاربر:"}{" "}
                                {h.percentage != null
                                  ? `${toPersianNumbers(h.percentage)}٪`
                                  : ""}{" "}
                                {h.installments_count != null
                                  ? `(${toPersianNumbers(h.installments_count)} قسط)`
                                  : ""}
                              </span>
                              <span className="text-[10px] text-slate-400">
                                {h.notes || ""}
                              </span>
                            </div>
                          ))}
                        </div>
                      );
                    }
                  } catch (e) {}
                  return null;
                })()}
            </div>

            <form onSubmit={handleSendProposal} className="p-5 space-y-4">
              <div className="space-y-1.5">
                <SearchableSelect
                  label="نوع وام"
                  options={loanTypes.map((t) => ({
                    value: t.id,
                    label: t.name,
                  }))}
                  value={approveData.loan_type_id}
                  onChange={(val) =>
                    setApproveData({ ...approveData, loan_type_id: val })
                  }
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
                    درصد کارمزد پیشنهادی مدیر (حداکثر ۱۵٪)
                  </label>
                  <input
                    type="text"
                    required
                    value={toPersianNumbers(approveData.percentage)}
                    onChange={(e) => {
                      const rawValue = toEnglishNumbers(e.target.value);
                      if (/^\d*\.?\d*$/.test(rawValue)) {
                        const num = parseFloat(rawValue || "0");
                        if (!rawValue || (num >= 0 && num <= 15)) {
                          setApproveData({
                            ...approveData,
                            percentage: rawValue,
                          });
                        }
                      }
                    }}
                    className="w-full h-10 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-3 text-xs focus:outline-none focus:border-persian-500 focus:ring-2 focus:ring-persian-50 dark:text-white"
                    placeholder="مثلا ۴"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
                    تعداد اقساط پیشنهادی مدیر (۱ تا ۳۶ ماه)
                  </label>
                  <input
                    type="text"
                    required
                    value={toPersianNumbers(approveData.installments_count)}
                    onChange={(e) => {
                      const rawValue = toEnglishNumbers(e.target.value);
                      if (/^\d*$/.test(rawValue)) {
                        const num = parseInt(rawValue || "0", 10);
                        if (!rawValue || (num >= 0 && num <= 36)) {
                          setApproveData({
                            ...approveData,
                            installments_count: rawValue,
                          });
                        }
                      }
                    }}
                    className="w-full h-10 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-3 text-xs focus:outline-none focus:border-persian-500 focus:ring-2 focus:ring-persian-50 dark:text-white"
                    placeholder="مثلا ۱۲"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
                  کارمزد ثبت‌نام اولیه (درصد)
                </label>
                <input
                  type="text"
                  required
                  value={toPersianNumbers(
                    approveData.registration_fee_percentage,
                  )}
                  onChange={(e) => {
                    const val = toEnglishNumbers(e.target.value);
                    if (/^\d*\.?\d*$/.test(val)) {
                      setApproveData({
                        ...approveData,
                        registration_fee_percentage: val,
                      });
                    }
                  }}
                  className="w-full h-10 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-3 text-xs focus:outline-none focus:border-persian-500 focus:ring-2 focus:ring-persian-50 dark:text-white"
                  placeholder="مثلا ۱"
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
                  نظر یا پیام مدیر برای متقاضی (اختیاری)
                </label>
                <textarea
                  rows={2}
                  value={approveData.admin_notes}
                  onChange={(e) =>
                    setApproveData({
                      ...approveData,
                      admin_notes: e.target.value,
                    })
                  }
                  className="w-full bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl p-2.5 text-xs focus:outline-none focus:border-persian-500 focus:ring-2 focus:ring-persian-50 dark:text-white"
                  placeholder="توضیحاتی برای متقاضی جهت اطلاع از دلایل انتخاب این شرایط..."
                />
              </div>

              <div className="pt-2 flex gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-persian-600 hover:bg-persian-700 text-white h-11 rounded-xl text-xs font-bold transition-all shadow-md shadow-persian-900/20 flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <Sparkles className="w-4 h-4" />
                  <span>ارسال شرایط به متقاضی (جهت بررسی و تایید)</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsApproveModalOpen(false)}
                  className="px-5 bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-200 h-11 rounded-xl text-xs font-medium transition-colors cursor-pointer"
                >
                  انصراف
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Final Approval Modal (Admin Finalizing Registration) */}
      {isFinalModalOpen && selectedLoanForFinal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-md my-8 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="p-5 border-b border-slate-200 dark:border-slate-700 bg-emerald-50 dark:bg-emerald-950/40 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-slate-900 dark:text-white">
                    تایید نهایی ثبت نام وام توسط مدیریت
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    متقاضی شرایط پیشنهادی را تایید کرده است
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsFinalModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg"
              >
                ✕
              </button>
            </div>

            <div className="p-5 space-y-4 text-xs">
              <div className="p-3.5 bg-slate-50 dark:bg-slate-900/50 rounded-xl space-y-2 border border-slate-200 dark:border-slate-700">
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">متقاضی:</span>
                  <span className="font-bold text-slate-900 dark:text-white">
                    {selectedLoanForFinal.first_name}{" "}
                    {selectedLoanForFinal.last_name}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">مبلغ وام:</span>
                  <span className="font-bold text-persian-600 dark:text-persian-400">
                    {formatCurrency(selectedLoanForFinal.amount)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">کارمزد تاییدشده:</span>
                  <span className="font-bold text-slate-900 dark:text-white">
                    {toPersianNumbers(selectedLoanForFinal.percentage ?? 0)}٪
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">اقساط تاییدشده:</span>
                  <span className="font-bold text-slate-900 dark:text-white">
                    {toPersianNumbers(
                      selectedLoanForFinal.installments_count ?? 0,
                    )}{" "}
                    ماه
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-slate-200 dark:border-slate-700">
                  <span className="text-slate-500">
                    کارمزد اولیه (
                    {toPersianNumbers(
                      selectedLoanForFinal.registration_fee_percentage ?? 0,
                    )}
                    ٪):
                  </span>
                  <span className="font-bold text-emerald-700 dark:text-emerald-400">
                    {formatCurrency(
                      (Number(selectedLoanForFinal.amount) *
                        (selectedLoanForFinal.registration_fee_percentage ||
                          0)) /
                        100,
                    )}
                  </span>
                </div>
                {selectedLoanForFinal.user_notes && (
                  <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
                    <span className="text-slate-500 block mb-0.5">
                      پیام متقاضی هنگام تایید:
                    </span>
                    <span className="text-slate-700 dark:text-slate-300 font-medium">
                      {selectedLoanForFinal.user_notes}
                    </span>
                  </div>
                )}
              </div>

              <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/50 rounded-xl text-blue-800 dark:text-blue-300 leading-relaxed text-[11px]">
                با تایید نهایی، پیامک/پیام بله با مشخصات کارت و شماره شبا جهت
                واریز کارمزد برای متقاضی ارسال می‌گردد و درخواست جهت دریافت و
                تایید فیش واریزی به بخش <strong>«دریافت فیش کارمزد»</strong>{" "}
                منتقل خواهد شد.
              </div>

              <div className="pt-2 flex gap-3">
                <button
                  type="button"
                  onClick={handleConfirmFinalApproval}
                  disabled={finalSubmitting}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white h-11 rounded-xl text-xs font-bold transition-all shadow-md shadow-emerald-900/20 flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  {finalSubmitting ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4" />
                  )}
                  <span>تایید نهایی و صدور فیش واریز</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsFinalModalOpen(false)}
                  disabled={finalSubmitting}
                  className="px-5 bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-200 h-11 rounded-xl text-xs font-medium transition-colors cursor-pointer"
                >
                  انصراف
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ ...modalState, isOpen: false })}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
      />
    </div>
  );
}
